import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import ErrorBoundary from "@/components/ErrorBoundary";
import { QuoteButton } from "@/components/QuoteButton";
import { discountRate, lineTotal } from "@/lib/pricing";
import { DeliveryForm } from "@/components/DeliveryForm";
import { deliveryError, deliveryFee } from "@/lib/delivery";

const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

export default function Checkout() {
  const { entries, total, removeItem, setQuantity, clearCart } = useCart();
  const [loading, setLoading] = useState("");
  const [paypalClientId, setPaypalClientId] = useState("");
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [delivery, setDelivery] = useState({ method: "", full_name: "", phone: "", address: "", city: "", region: "", postal_code: "", country: "", notes: "" });

  useEffect(() => { api.get("/config").then(({ data }) => setPaypalClientId(data.paypal_client_id || "")).catch(() => {}); }, []);

  const items = entries.map((e) => ({ id: e.key, quantity: e.quantity, sku: e.key }));
  const itemsTotal = Math.round(entries.reduce((s, e) => s + lineTotal(e.productId, e.price, e.quantity), 0) * 100) / 100;
  const savings = Math.round((total - itemsTotal) * 100) / 100;
  const fee = deliveryFee(delivery.method);
  const payable = Math.round((itemsTotal + fee) * 100) / 100;
  const dErr = deliveryError(delivery);
  const quoteItems = entries.map((e) => ({ name: `${e.productName} — ${e.itemName}`, quantity: e.quantity, sku: e.key }));
  const origin_url = window.location.origin;
  const body = { items, origin_url, currency: "usd", email: email.trim() || undefined, delivery };
  const bodyRef = useRef(body);
  bodyRef.current = body;
  const apiError = (e, fallback) => e?.response?.data?.detail || fallback;

  const payStripe = async () => {
    if (!items.length || dErr) return;
    setLoading("stripe"); setError("");
    try {
      const { data } = await api.post("/checkout/session", body);
      if (data.url) window.location.href = data.url;
      else { setError("Could not start checkout."); setLoading(""); }
    } catch (e) { setError(apiError(e, "Could not start Stripe checkout.")); setLoading(""); }
  };
  const payPesapal = async () => {
    if (!items.length || dErr) return;
    setLoading("pesapal"); setError("");
    try {
      const { data } = await api.post("/pesapal/checkout", body);
      if (data.redirect_url) { window.location.href = data.redirect_url; return; }
      setError(data.error || "Could not start Pesapal checkout."); setLoading("");
    } catch (e) { setError(apiError(e, "Could not start Pesapal checkout.")); setLoading(""); }
  };

  if (entries.length === 0) {
    return (
      <section className="min-h-[60vh] bg-[#0A0F0B] px-6 py-28 text-center">
        <div className="font-mono text-xs tracking-widest text-brand-green-light">YOUR CART</div>
        <h1 className="mt-3 font-display text-6xl font-black text-brand-light">YOUR CART IS EMPTY</h1>
        <p className="mx-auto mt-4 max-w-md text-brand-muted">Browse product lines, choose the item that fits your needs, and add it to your cart.</p>
        <Link to="/shop" className="mt-8 inline-block rounded-xl bg-brand-green px-8 py-4 font-bold text-brand-light">Browse the shop</Link>
      </section>
    );
  }

  return (
    <section className="bg-[#0A0F0B] px-6 py-16 md:px-14">
      <div className="mx-auto max-w-7xl">
        <div className="mb-10">
          <div className="font-mono text-xs tracking-widest text-brand-green-light">SECURE CHECKOUT</div>
          <h1 className="mt-3 font-display text-6xl font-black text-brand-light">CHECKOUT</h1>
          <p className="mt-3 max-w-2xl text-brand-muted">Review your items, then pay securely by card (Stripe), PayPal, or Pesapal (cards & mobile money).</p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="space-y-4">
            {entries.map((entry) => (
              <article key={entry.key} className="grid grid-cols-[96px_1fr] gap-5 rounded-2xl border border-brand-green/15 bg-brand-mid p-4 sm:grid-cols-[112px_1fr_auto]">
                {entry.image ? <img src={entry.image} alt="" className="h-24 w-24 rounded-xl object-cover sm:h-28 sm:w-28" /> : <div className="h-24 w-24 rounded-xl bg-brand-navy sm:h-28 sm:w-28" />}
                <div className="self-center">
                  <div className="text-xs font-bold uppercase tracking-widest text-brand-green-light">{entry.productName}</div>
                  <h2 className="mt-1 font-display text-2xl font-bold text-brand-light">{entry.itemName}</h2>
                  <div className="mt-2 text-sm text-brand-muted">{currency.format(entry.price)} each</div>
                  <div className="mt-3 flex items-center gap-3">
                    <label className="text-xs text-brand-muted">Qty
                      <input type="number" min="1" value={entry.quantity} onChange={(e) => setQuantity(entry.key, Math.max(1, Number(e.target.value)))} className="ml-2 w-16 rounded-lg border border-brand-green/20 bg-brand-navy px-2 py-1.5 text-brand-light" /></label>
                    <button type="button" onClick={() => removeItem(entry.key)} className="text-xs font-semibold text-brand-muted hover:text-brand-light">Remove</button>
                  </div>
                </div>
                <div className="col-span-2 self-center text-right sm:col-span-1">
                  {discountRate(entry.productId, entry.quantity) > 0 && (
                    <div className="text-sm text-brand-muted line-through">{currency.format(entry.price * entry.quantity)}</div>
                  )}
                  <div className="font-display text-2xl font-black text-brand-light" data-testid={`line-total-${entry.key}`}>{currency.format(lineTotal(entry.productId, entry.price, entry.quantity))}</div>
                  {discountRate(entry.productId, entry.quantity) > 0 && (
                    <div className="text-xs font-bold text-brand-green-light" data-testid={`line-discount-${entry.key}`}>{discountRate(entry.productId, entry.quantity) * 100}% bulk discount</div>
                  )}
                </div>
              </article>
            ))}
            <DeliveryForm value={delivery} onChange={setDelivery} />
            <div className="flex items-center justify-between pt-2">
              <Link to="/shop" className="text-sm font-semibold text-brand-green-light hover:underline">← Continue shopping</Link>
              <button type="button" onClick={clearCart} className="text-sm text-brand-muted hover:text-brand-light">Clear cart</button>
            </div>
          </div>

          <div className="h-fit rounded-2xl border border-brand-green/20 bg-brand-mid p-7 lg:sticky lg:top-32">
            <h2 className="font-display text-3xl font-black text-brand-light">ORDER SUMMARY</h2>
            <div className="mt-5 flex justify-between border-t border-brand-green/10 pt-5">
              <span className="text-brand-muted">Subtotal</span>
              <span className="text-brand-light">{currency.format(total)}</span>
            </div>
            {savings > 0 && (
              <div className="mt-2 flex justify-between text-sm" data-testid="cart-savings">
                <span className="text-brand-green-light">Bulk discount</span>
                <span className="text-brand-green-light">−{currency.format(savings)}</span>
              </div>
            )}
            <div className="mt-2 flex justify-between text-sm" data-testid="cart-delivery-fee">
              <span className="text-brand-muted">Delivery</span>
              <span className="text-brand-light">{delivery.method ? (fee ? currency.format(fee) : "Free") : "Choose method"}</span>
            </div>
            <div className="mt-3 flex justify-between border-b border-brand-green/10 pb-5">
              <span className="text-brand-muted">Total</span>
              <span className="font-display text-3xl font-black text-brand-light" data-testid="cart-total">{currency.format(payable)}</span>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-brand-muted">Bulk hardware pricing: 10+ units of an item save 5%, 25+ units save 10%.</p>
            <p className="mt-2 text-xs leading-relaxed text-brand-muted">Installation and configuration are confirmed by Jero-One after your order.</p>
            {dErr && <p className="mt-3 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-200" data-testid="delivery-error">{dErr}</p>}
            {error && <p className="mt-3 text-xs text-red-400" data-testid="checkout-error">{error}</p>}
            <label className="mt-5 block text-xs font-mono tracking-widest text-brand-muted">EMAIL FOR YOUR RECEIPT
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="checkout-email-input" className="mt-2 w-full rounded-xl border border-brand-green/20 bg-brand-navy px-4 py-3 font-sans text-sm tracking-normal text-brand-light placeholder:text-brand-muted/60 focus:border-brand-green focus:outline-none" />
            </label>
            <div className="mt-6 space-y-3">
              <button type="button" onClick={payStripe} disabled={!!loading || !!dErr} data-testid="cart-drawer-checkout" className="w-full rounded-xl bg-brand-green px-6 py-4 font-bold text-brand-light transition-colors hover:bg-brand-green-light disabled:opacity-60">
                {loading === "stripe" ? "Redirecting…" : "Pay by Card (Stripe)"}
              </button>
              <button type="button" onClick={payPesapal} disabled={!!loading || !!dErr} data-testid="cart-drawer-pesapal" className="w-full rounded-xl border border-brand-green/40 px-6 py-4 font-bold text-brand-light transition-colors hover:border-brand-green disabled:opacity-60">
                {loading === "pesapal" ? "Redirecting…" : "Pay with Pesapal (Cards & Mobile Money)"}
              </button>
              {paypalClientId && (
                <ErrorBoundary silent fallback={null}>
                  <div data-testid="paypal-buttons" className="pt-1">
                    <PayPalScriptProvider options={{ clientId: paypalClientId, currency: "USD", intent: "capture" }}>
                      <PayPalButtons style={{ layout: "horizontal", color: "gold", shape: "pill", height: 45, tagline: false }} disabled={!!loading || !!dErr} forceReRender={[payable, entries.length, !!dErr]}
                        createOrder={async () => { try { const { data } = await api.post("/paypal/create-order", bodyRef.current); return data.id; } catch (e) { setError(apiError(e, "Could not start PayPal checkout.")); throw e; } }}
                        onApprove={async (data) => { const res = await api.post(`/paypal/capture-order/${data.orderID}`); if (res.data.paid) { clearCart(); window.location.href = "/checkout/status?paypal=success"; } else { setError("Payment not completed."); } }}
                        onError={() => setError("PayPal payment failed. Please try another method.")} />
                    </PayPalScriptProvider>
                  </div>
                </ErrorBoundary>
              )}
            </div>
            <div className="mt-6 border-t border-brand-green/10 pt-5 text-center">
              <p className="mb-3 text-xs text-brand-muted">Large or project order? Get tailored pricing.</p>
              <QuoteButton items={quoteItems} className="w-full" testid="cart-request-quote-btn" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
